var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { krBaseCSS } from '../style/base.js';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
/**
 * Code demo component with Preview/Code tabs and syntax highlighting.
 *
 * Usage:
 * ```html
 * <kr-code-demo language="html">
 *   <kr-button>Click me</kr-button>
 * </kr-code-demo>
 * ```
 *
 * Requires highlight.js to be loaded globally for syntax highlighting.
 */
export class KRCodeDemo extends LitElement {
    constructor() {
        super(...arguments);
        this.language = 'html';
        this.code = '';
        this.activeTab = 'preview';
        this.copied = false;
        this.highlightedCode = '';
    }
    connectedCallback() {
        super.connectedCallback();
        // Defer to next frame so the parser finishes with children before we read innerHTML
        requestAnimationFrame(() => {
            if (!this.code) {
                // innerHTML normalizes boolean attributes (e.g. dismissible -> dismissible=""), strip the ="" for cleaner display
                this.code = this.innerHTML.trim().replace(/=""(?=[\s>])/g, '');
            }
            this.querySelectorAll('script').forEach(oldScript => {
                const newScript = document.createElement('script');
                newScript.textContent = oldScript.textContent;
                oldScript.replaceWith(newScript);
            });
            if (this.code && window.hljs && window.hljs.getLanguage(this.language)) {
                this.highlightedCode = window.hljs.highlight(this.code, { language: this.language }).value;
            }
            else {
                this.highlightedCode = this.code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            }
        });
    }
    activateTab(tab) {
        this.activeTab = tab;
    }
    handleCopyClick() {
        if (!this.code) {
            return;
        }
        navigator.clipboard.writeText(this.code).then(() => {
            this.copied = true;
            setTimeout(() => {
                this.copied = false;
            }, 2000);
        });
    }
    render() {
        return html `
      <div class="tabs">
        <button
          class=${classMap({
            tab: true,
            'tab--active': this.activeTab === 'preview'
        })}
          @click=${() => this.activateTab('preview')}
        >
          Preview
        </button>
        <button
          class=${classMap({
            tab: true,
            'tab--active': this.activeTab === 'code'
        })}
          @click=${() => this.activateTab('code')}
        >
          Code
        </button>
        <button
          class=${classMap({
            'copy': true,
            'copy--success': this.copied
        })}
          @click=${this.handleCopyClick}
          title=${this.copied ? 'Copied!' : 'Copy code'}
        >
          ${this.copied
            ? html `
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            `
            : html `
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect
                  x="9"
                  y="9"
                  width="13"
                  height="13"
                  rx="2"
                  ry="2"
                ></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            `}
        </button>
      </div>

      <div class=${classMap({
            panel: true,
            'panel--active': this.activeTab === 'preview',
            'panel--preview': true
        })}>
        <slot></slot>
      </div>

      <div class=${classMap({
            panel: true,
            'panel--active': this.activeTab === 'code',
            'panel--code': true
        })}>
        <pre class="code"><code>${unsafeHTML(this.highlightedCode)}</code></pre>
      </div>

    `;
    }
}
KRCodeDemo.styles = [krBaseCSS, css `
    :host {
      display: block;
      border: 1px solid #d5dbdb;
      border-radius: 8px;
      overflow: visible;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .tabs {
      display: flex;
      align-items: center;
      height: 48px;
      background: #f8f9fa;
      border-bottom: 1px solid #d5dbdb;
      border-radius: 8px 8px 0 0;
    }

    .tab {
      height: 100%;
      padding: 0 20px;
      background: none;
      border: none;
      color: #6c757d;
      font-size: 14px;
      font-weight: 500;
      font-family: inherit;
      cursor: pointer;
      position: relative;
      transition: color 0.15s;
    }

    .tab:hover {
      color: #495057;
    }

    .tab--active {
      color: #1a1a2e;
    }

    .tab--active::after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--kr-primary);
    }

    .panel {
      display: none;
    }

    .panel--active {
      display: block;
    }

    .panel--preview {
      padding: 24px;
      background: #ffffff;
    }

    .panel--code {
    }

    .code {
      margin: 0;
      padding: 20px;
      background: #ffffff;
      overflow-x: auto;
      font-family: 'SF Mono', 'Fira Code', 'Monaco', 'Consolas', monospace;
      font-size: 13px;
      line-height: 1.6;
      color: #1a1a2e;
      border-radius: 0 0 8px 8px;
    }

    .code code {
      font-family: inherit;
    }

    .copy {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: auto;
      margin-right: 12px;
      padding: 0;
      background: none;
      border: none;
      border-radius: 4px;
      color: #6c757d;
      cursor: pointer;
      transition: color 0.15s;
    }

    .copy:hover {
      color: #495057;
    }

    .copy--success {
      color: #16a34a;
    }

    /* Syntax highlighting */
    .hljs-tag { color: #22863a; }
    .hljs-name { color: #22863a; }
    .hljs-attr { color: #6f42c1; }
    .hljs-string { color: #032f62; }
    .hljs-comment { color: #6a737d; font-style: italic; }
    .hljs-keyword { color: #d73a49; }
    .hljs-number { color: #005cc5; }
    .hljs-built_in { color: #e36209; }
    .hljs-literal { color: #005cc5; }
    .hljs-symbol { color: #e36209; }
    .hljs-selector-tag { color: #22863a; }
    .hljs-selector-class { color: #6f42c1; }
    .hljs-selector-id { color: #005cc5; }
    .hljs-attribute { color: #005cc5; }
    .hljs-property { color: #005cc5; }

  `];
__decorate([
    property({ type: String })
], KRCodeDemo.prototype, "language", void 0);
__decorate([
    state()
], KRCodeDemo.prototype, "code", void 0);
__decorate([
    state()
], KRCodeDemo.prototype, "activeTab", void 0);
__decorate([
    state()
], KRCodeDemo.prototype, "copied", void 0);
__decorate([
    state()
], KRCodeDemo.prototype, "highlightedCode", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-code-demo')) {
    customElements.define('kr-code-demo', KRCodeDemo);
}
//# sourceMappingURL=code-demo.js.map